import { Injectable } from '@angular/core';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { LocalStorageKeys } from 'src/app/enum/local-storage-keys.enum';
import { jwtDecode } from 'jwt-decode';

/**
 * Servicio para validar expiración de sesión basada en business rules.
 * 
 * ⚠️ IMPORTANTE - Expiración de Tokens:
 * Este servicio valida la expiración de la SESIÓN (business rule de 48h),
 * NO la expiración real del JWT que Azure AD gestiona.
 * 
 * - Expiración real del JWT: Gestionada por Azure AD (~1 hora típicamente)
 * - Validada automáticamente por MSAL en acquireTokenSilent()
 * - Codificada en el campo 'exp' del JWT
 * 
 * - Expiración de sesión (48h): Política de la aplicación
 * - Guardada en localStorage como fecha ISO
 * - Fuerza re-login periódico como medida de seguridad
 * - NO sustituye la validación real del JWT
 */
@Injectable({
  providedIn: 'root',
})
export class TokenExpirationService {
  private readonly BUFFER_MINUTES = 5; // Considerar expirado 5 min antes

  constructor(private localStorageService: LocalStorageService) {}

  /**
   * Comprueba si la SESIÓN ha expirado según la business rule (48h).
   * 
   * ⚠️ IMPORTANTE: Esto NO valida la expiración real del JWT.
   * Azure AD y MSAL gestionan la expiración real del access token.
   * 
   * @returns true si han pasado más de 48h desde el login
   */
  isExpired(): boolean {
    const expirationStr = this.localStorageService.get(
      LocalStorageKeys.TOKENEXPIRATION
    ) as string;
    
    if (!expirationStr) {
      return true; // Sin fecha = expirado
    }

    const expirationDate = new Date(expirationStr);
    const now = new Date();
    const isExpired = now > expirationDate;
    return isExpired;
  }

  /**
   * Comprueba si la SESIÓN está próxima a expirar (business rule).
   * 
   * ⚠️ IMPORTANTE: Esto NO valida la expiración real del JWT.
   * 
   * @param minutesBefore - Minutos antes de la expiración de sesión
   * @returns true si la sesión expira en menos de X minutos
   */
  isAboutToExpire(minutesBefore: number = this.BUFFER_MINUTES): boolean {
    const expirationStr = this.localStorageService.get(
      LocalStorageKeys.TOKENEXPIRATION
    ) as string;
    
    if (!expirationStr) {
      return true;
    }

    const expirationDate = new Date(expirationStr);
    const now = new Date();
    const diffMs = expirationDate.getTime() - now.getTime();
    const diffMinutes = diffMs / (1000 * 60);

    return diffMinutes < minutesBefore;
  }

  /**
   * Decodifica un JWT y comprueba su expiración
   * Útil cuando tienes el JWT directamente
   */
  isJwtExpired(jwt: string): boolean {
    try {
      const decodedToken: any = jwtDecode(jwt);
      const expInSeconds = decodedToken.exp;
      const nowInSeconds = Math.floor(Date.now() / 1000);
      return nowInSeconds > expInSeconds;
    } catch (error) {
      return true; // Si no se puede decodificar, considera expirado
    }
  }

  /**
   * Obtiene el tiempo restante en milisegundos
   */
  getTimeRemaining(): number {
    const expirationStr = this.localStorageService.get(
      LocalStorageKeys.TOKENEXPIRATION
    ) as string;
    
    if (!expirationStr) {
      return 0;
    }

    const expirationDate = new Date(expirationStr);
    return Math.max(0, expirationDate.getTime() - Date.now());
  }

  /**
   * Obtiene el tiempo restante en minutos
   */
  getTimeRemainingMinutes(): number {
    return Math.ceil(this.getTimeRemaining() / (1000 * 60));
  }
}
